import { useEffect, useId, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import {
  AppWindow,
  ArrowLeft,
  Blocks,
  Calendar,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  Crop,
  Dog,
  ExternalLink,
  Eye,
  EyeOff,
  Globe2,
  GripVertical,
  Home,
  Image as ImageIcon,
  Images,
  Keyboard,
  ListChecks,
  Mail,
  Monitor,
  MousePointer2,
  Palette,
  PanelBottom,
  PanelLeft,
  Plus,
  Quote,
  Redo2,
  Rows3,
  Settings2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Store,
  Trash2,
  Type,
  Undo2,
  Video,
  X,
  type LucideIcon,
} from 'lucide-react';
import { CmsProvider, useCms } from '../admin/CmsContext';
import { Login } from '../admin/Login';
import { adminApi } from '../../lib/adminApi';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { defaultSiteContent, sugarloafPlaceholderDogs } from '../../content/defaultSiteContent';
import { isFacebookSectionVisible, normalizeFacebookUrl, setFacebookSectionVisibility } from '../../content/facebook';
import { normalizeImageCrop } from '../../content/imageCrop';
import type {
  ImageCrop,
  LineageDog,
  SiteContent,
  SiteSectionId,
  ThemeButtons,
  ThemeColors,
  ThemeLayout,
  ThemeTypography,
} from '../../content/types';
import type { Litter } from '../../lib/api';
import { ImageCropModal } from './ImageCropModal';
import { MediaModal } from './MediaModal';

const previewLitters: Litter[] = [
  { id: 'preview', title: 'Vixen × Apollo', description: 'Classic structure, steady family temperaments, full clearances.', expectedDate: '2026-09-15', spotsLeft: 3, status: 'available', imageUrl: '/images/bento_puppies.jpg' },
];

type SectionGroup = 'header' | 'template' | 'footer';
type SectionMeta = { id: SiteSectionId; name: string; group: SectionGroup; icon: LucideIcon };

const EDITOR_SECTIONS: SectionMeta[] = [
  { id: 'hero', name: 'Navigation & hero', group: 'header', icon: ImageIcon },
  { id: 'dogs', name: 'Our dogs', group: 'template', icon: Dog },
  { id: 'health', name: 'Health standards', group: 'template', icon: ShieldCheck },
  { id: 'weeks', name: 'First eight weeks', group: 'template', icon: CalendarDays },
  { id: 'gallery', name: 'Gallery', group: 'template', icon: Images },
  { id: 'process', name: 'How it works', group: 'template', icon: ListChecks },
  { id: 'stories', name: 'Testimonials', group: 'template', icon: Quote },
  { id: 'quiz', name: 'Readiness quiz', group: 'template', icon: ClipboardCheck },
  { id: 'litters', name: 'Upcoming litters', group: 'template', icon: Calendar },
  { id: 'faq', name: 'Frequently asked', group: 'template', icon: CircleHelp },
  { id: 'facebook', name: 'Facebook videos', group: 'template', icon: Video },
  { id: 'contact', name: 'Contact form', group: 'template', icon: Mail },
  { id: 'footer', name: 'Footer & newsletter', group: 'footer', icon: PanelBottom },
];

const SECTION_IDS = new Set<SiteSectionId>(EDITOR_SECTIONS.map((section) => section.id));
const isSectionId = (value: unknown): value is SiteSectionId => typeof value === 'string' && SECTION_IDS.has(value as SiteSectionId);
const sectionMeta = (id: SiteSectionId) => EDITOR_SECTIONS.find((section) => section.id === id)!;

type ThemeCat = 'colors' | 'typography' | 'layout' | 'buttons';
const THEME_CATS: Array<{ id: ThemeCat; name: string; description: string; icon: LucideIcon }> = [
  { id: 'colors', name: 'Colors', description: 'Brand palette and surfaces', icon: Palette },
  { id: 'typography', name: 'Typography', description: 'Headings and body text', icon: Type },
  { id: 'layout', name: 'Layout', description: 'Width, spacing, and corners', icon: Rows3 },
  { id: 'buttons', name: 'Buttons', description: 'Shape and primary color', icon: AppWindow },
];

const THEME_COLORS: Array<{ key: keyof ThemeColors; label: string; hint: string }> = [
  { key: 'orange', label: 'Accent', hint: 'Links, kickers, and highlights' },
  { key: 'ink', label: 'Text', hint: 'Headings and primary copy' },
  { key: 'cream', label: 'Background', hint: 'Main page background' },
  { key: 'sand', label: 'Panels', hint: 'Alternate sections and soft fills' },
  { key: 'muted', label: 'Muted text', hint: 'Secondary copy and captions' },
  { key: 'line', label: 'Borders', hint: 'Dividers and card outlines' },
];

const HERO_CROP_DEFAULT: ImageCrop = { x: 50, y: 50, zoom: 1 };
const DOG_CROP_DEFAULT: ImageCrop = { x: 50, y: 25, zoom: 1 };
const GALLERY_CROP_DEFAULT: ImageCrop = { x: 50, y: 50, zoom: 1 };
const LITTER_CROP_DEFAULT: ImageCrop = { x: 50, y: 30, zoom: 1 };

type CropEditorState = {
  src: string;
  label: string;
  aspectRatio: number;
  value?: ImageCrop | null;
  defaultValue: ImageCrop;
  apply: (crop: ImageCrop | null) => void;
};

function imageCropStyle(crop?: ImageCrop | null): CSSProperties | undefined {
  const normalized = normalizeImageCrop(crop);
  if (!normalized) return undefined;
  return {
    objectPosition: `${normalized.x}% ${normalized.y}%`,
    transform: `scale(${normalized.zoom})`,
    transformOrigin: `${normalized.x}% ${normalized.y}%`,
  };
}

function copyDogProfile(dog: LineageDog): LineageDog {
  return {
    ...dog,
    image: { ...dog.image },
    health: [...dog.health],
    awards: [...dog.awards],
    pedigree: dog.pedigree ? {
      ...dog.pedigree,
      sireGrands: [...dog.pedigree.sireGrands],
      damGrands: [...dog.pedigree.damGrands],
    } : undefined,
  };
}

function uniqueDogProfileId(base: string, dogs: LineageDog[]) {
  const slug = base.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'dog';
  if (!dogs.some((dog) => dog.id === slug)) return slug;
  let suffix = 2;
  while (dogs.some((dog) => dog.id === `${slug}-${suffix}`)) suffix += 1;
  return `${slug}-${suffix}`;
}

function newDogProfile(dogs: LineageDog[]): LineageDog {
  return {
    id: uniqueDogProfileId('new-dog', dogs),
    name: 'New dog',
    registeredName: '',
    title: 'Profile details pending',
    image: { src: '/images/about.jpg', alt: 'Placeholder image for a new dog profile' },
    description: 'Add this dog’s verified profile details and story here before publishing.',
    health: [],
    awards: [],
  };
}

type Selected = { kind: 'section'; id: SiteSectionId } | { kind: 'theme'; id: ThemeCat } | null;
type EditorMode = 'sections' | 'theme' | 'embeds';
type MenuId = 'theme' | 'audience' | 'template' | 'assistant' | null;

export function ThemeEditorApp() {
  useDocumentTitle('Theme editor | Sugarloaf');
  const [status, setStatus] = useState<'loading' | 'in' | 'out'>('loading');

  useEffect(() => {
    adminApi.getSession().then((session) => setStatus(session.authenticated ? 'in' : 'out')).catch(() => setStatus('out'));
  }, []);

  if (status === 'loading') return <div className="ed-loading"><span className="ed-spinner" />Loading editor…</div>;
  if (status === 'out') return <Login onAuthenticated={() => setStatus('in')} />;
  return <CmsProvider><EditorGate /></CmsProvider>;
}

function EditorGate() {
  const cms = useCms();
  if (!cms.loaded) return <div className="ed-loading"><span className="ed-spinner" />Preparing your site…</div>;
  return <Editor />;
}

function Editor() {
  const { content, setContent, litters, setLitters, save, saving } = useCms();
  const requestedSection = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('section') : null;
  const initialSection = isSectionId(requestedSection) ? requestedSection : null;
  const [mode, setMode] = useState<EditorMode | null>('sections');
  const [selected, setSelected] = useState<Selected>(initialSection ? { kind: 'section', id: initialSection } : null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [inspector, setInspector] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [picker, setPicker] = useState<{ apply: (url: string) => void; dogId?: string } | null>(null);
  const [cropper, setCropper] = useState<CropEditorState | null>(null);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuId>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [dragging, setDragging] = useState<SiteSectionId | null>(null);
  const [collapsed, setCollapsed] = useState<Record<SectionGroup, boolean>>({ header: false, template: false, footer: false });
  const rightBodyRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const openCrop = (options: CropEditorState) => setCropper(options);

  const post = (message: unknown) => iframeRef.current?.contentWindow?.postMessage(message, window.location.origin);

  // Shopify-style local undo history. Content and litter inventory travel together.
  type EditorSnapshot = { content: SiteContent; litters: Litter[] };
  const history = useRef<EditorSnapshot[]>([{ content, litters }]);
  const historyIndex = useRef(0);
  const savedBaseline = useRef(JSON.stringify({ content, litters }));
  const traveling = useRef(false);
  const [, refreshHistory] = useState(0);
  const refresh = () => refreshHistory((value) => value + 1);

  useEffect(() => {
    if (traveling.current) {
      traveling.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      const snapshots = history.current.slice(0, historyIndex.current + 1);
      const nextSnapshot = { content, litters };
      if (JSON.stringify(snapshots[snapshots.length - 1]) !== JSON.stringify(nextSnapshot)) {
        snapshots.push(nextSnapshot);
        history.current = snapshots;
        historyIndex.current = snapshots.length - 1;
        refresh();
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [content, litters]);

  const undo = () => {
    if (historyIndex.current <= 0) return;
    traveling.current = true;
    historyIndex.current -= 1;
    const snapshot = history.current[historyIndex.current];
    setContent(() => snapshot.content);
    setLitters(() => snapshot.litters);
    refresh();
  };

  const redo = () => {
    if (historyIndex.current >= history.current.length - 1) return;
    traveling.current = true;
    historyIndex.current += 1;
    const snapshot = history.current[historyIndex.current];
    setContent(() => snapshot.content);
    setLitters(() => snapshot.litters);
    refresh();
  };

  const canUndo = historyIndex.current > 0;
  const canRedo = historyIndex.current < history.current.length - 1;
  const editorDirty = savedBaseline.current !== JSON.stringify({ content, litters });

  const onSave = async () => {
    setSaveError('');
    const ok = await save();
    if (!ok) {
      setSaveError('Changes could not be saved. Check your connection and try again.');
      return;
    }
    history.current = [{ content, litters }];
    historyIndex.current = 0;
    savedBaseline.current = JSON.stringify({ content, litters });
    refresh();
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2200);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey;
      if (modifier && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
      }
      if (modifier && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (editorDirty && !saving) void onSave();
      }
      if (event.key === 'Escape') {
        setMenu(null);
        setAddOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    if (!editorDirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editorDirty]);

  useEffect(() => {
    const closeMenus = (event: PointerEvent) => {
      if (!(event.target as Element).closest('.ed-menu-anchor')) setMenu(null);
      if (!(event.target as Element).closest('.ed-add-wrap')) setAddOpen(false);
    };
    document.addEventListener('pointerdown', closeMenus);
    return () => document.removeEventListener('pointerdown', closeMenus);
  }, []);

  const litterPreview = litters.length ? litters : previewLitters;

  const openCropForPreviewTarget = (target: unknown, measuredAspectRatio?: unknown) => {
    if (typeof target !== 'string') return;
    const previewAspectRatio = typeof measuredAspectRatio === 'number' && Number.isFinite(measuredAspectRatio) && measuredAspectRatio >= 0.2 && measuredAspectRatio <= 5
      ? measuredAspectRatio
      : null;
    const separator = target.indexOf(':');
    if (separator < 1) return;
    const kind = target.slice(0, separator);
    const key = target.slice(separator + 1);

    if (kind === 'heroPhotos' || kind === 'hero') {
      const index = Number(key);
      const src = content.heroPhotos[index];
      if (!Number.isInteger(index) || !src) return;
      setMode('sections');
      setSelected({ kind: 'section', id: 'hero' });
      setSettingsOpen(true);
      openCrop({
        src,
        label: `Hero photo ${index + 1}`,
        aspectRatio: previewAspectRatio ?? 4 / 3,
        value: content.heroPhotoCrops?.[index],
        defaultValue: HERO_CROP_DEFAULT,
        apply: (crop) => setContent((current) => ({
          ...current,
          heroPhotoCrops: current.heroPhotos.map((_, photoIndex) => photoIndex === index ? crop : (current.heroPhotoCrops?.[photoIndex] ?? null)),
        })),
      });
      return;
    }

    if (kind === 'dogs' || kind === 'dog') {
      const dog = content.lineage.find((item) => item.id === key);
      if (!dog?.image.src) return;
      setMode('sections');
      setSelected({ kind: 'section', id: 'dogs' });
      setSettingsOpen(true);
      openCrop({
        src: dog.image.src,
        label: `${dog.name || 'Dog'} photo`,
        aspectRatio: previewAspectRatio ?? 4 / 3,
        value: dog.image.crop,
        defaultValue: DOG_CROP_DEFAULT,
        apply: (crop) => setContent((current) => ({
          ...current,
          lineage: current.lineage.map((item) => item.id === key ? { ...item, image: { ...item.image, crop: crop ?? undefined } } : item),
        })),
      });
      return;
    }

    if (kind === 'gallery') {
      const index = Number(key);
      const item = content.gallery[index];
      if (!Number.isInteger(index) || !item?.src) return;
      setMode('sections');
      setSelected({ kind: 'section', id: 'gallery' });
      setSettingsOpen(true);
      openCrop({
        src: item.src,
        label: item.caption || `Gallery photo ${index + 1}`,
        aspectRatio: previewAspectRatio ?? (item.span === 2 ? 2 / 3 : 4 / 3),
        value: item.crop,
        defaultValue: GALLERY_CROP_DEFAULT,
        apply: (crop) => setContent((current) => ({
          ...current,
          gallery: current.gallery.map((entry, itemIndex) => itemIndex === index ? { ...entry, crop: crop ?? undefined } : entry),
        })),
      });
      return;
    }

    if (kind === 'litters' || kind === 'litter') {
      const litter = litters.find((item) => item.id === key);
      if (!litter?.imageUrl) return;
      setMode('sections');
      setSelected({ kind: 'section', id: 'litters' });
      setSettingsOpen(true);
      openCrop({
        src: litter.imageUrl,
        label: `${litter.title || 'Litter'} cover`,
        aspectRatio: previewAspectRatio ?? 16 / 9,
        value: litter.imageCrop,
        defaultValue: LITTER_CROP_DEFAULT,
        apply: (crop) => setLitters((current) => current.map((item) => item.id === key ? { ...item, imageCrop: crop } : item)),
      });
    }
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data || {};
      if (data.type === 'ed:ready') {
        post({ type: 'ed:content', content, litters: litterPreview });
        post({ type: 'ed:selected', section: selected?.kind === 'section' ? selected.id : null });
        post({ type: 'ed:inspector', enabled: inspector });
      }
      if (data.type === 'ed:select' && isSectionId(data.section)) {
        setMode('sections');
        setSelected({ kind: 'section', id: data.section });
        setSettingsOpen(true);
      }
      if (data.type === 'ed:field') {
        const [section, key] = String(data.field).split(':');
        if (!isSectionId(section)) return;
        setMode('sections');
        setSelected({ kind: 'section', id: section });
        setSettingsOpen(true);
        setScrollTarget(key || null);
      }
      if (data.type === 'ed:image') openCropForPreviewTarget(data.target, data.aspectRatio);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  });

  useEffect(() => { post({ type: 'ed:content', content, litters: litterPreview }); }, [content, litters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { post({ type: 'ed:selected', section: selected?.kind === 'section' ? selected.id : null }); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { post({ type: 'ed:inspector', enabled: inspector }); }, [inspector]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scrollTarget) return;
    const timer = window.setTimeout(() => {
      const field = rightBodyRef.current?.querySelector(`[data-field="${scrollTarget}"]`) as HTMLElement | null;
      if (field) {
        field.scrollIntoView({ block: 'center', behavior: 'smooth' });
        (field.querySelector('input, textarea, select') as HTMLElement | null)?.focus();
      }
      setScrollTarget(null);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [scrollTarget, selected]);

  const deviceWidth = device === 'mobile' ? 390 : 1440;
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    const compute = () => setScale(Math.min(1, Math.max(0.25, (pane.clientWidth - 48) / deviceWidth)));
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [deviceWidth, mode, selected, settingsOpen]);

  const selectSection = (id: SiteSectionId) => {
    setMode('sections');
    setSelected({ kind: 'section', id });
    setSettingsOpen(true);
  };

  const selectThemeSetting = (id: ThemeCat) => {
    setMode('theme');
    setSelected({ kind: 'theme', id });
    setSettingsOpen(true);
  };

  const toggleMode = (next: EditorMode) => {
    if (mode === next) {
      setMode(null);
      setSelected(null);
      setSettingsOpen(false);
      return;
    }
    setMode(next);
    setSelected(null);
    setSettingsOpen(false);
  };

  const toggleHidden = (id: SiteSectionId) => {
    setContent((current) => {
      if (id === 'facebook') return setFacebookSectionVisibility(current, !isFacebookSectionVisible(current));
      const hidden = current.sections.hidden.includes(id)
        ? current.sections.hidden.filter((section) => section !== id)
        : [...current.sections.hidden, id];
      return { ...current, sections: { ...current.sections, hidden } };
    });
  };

  const moveSection = (from: SiteSectionId, to: SiteSectionId) => {
    if (from === to || sectionMeta(from).group !== 'template' || sectionMeta(to).group !== 'template') return;
    setContent((current) => {
      const order = [...current.sections.order];
      const fromIndex = order.indexOf(from);
      const toIndex = order.indexOf(to);
      if (fromIndex === -1 || toIndex === -1) return current;
      order.splice(fromIndex, 1);
      order.splice(toIndex, 0, from);
      return { ...current, sections: { ...current.sections, order } };
    });
  };

  const openPicker = (apply: (url: string) => void, dogId?: string) => setPicker({ apply, dogId });
  const hiddenTemplateSections = EDITOR_SECTIONS.filter((section) => section.group === 'template' && (section.id === 'facebook' ? !isFacebookSectionVisible(content) : content.sections.hidden.includes(section.id)));
  const selectedTitle = selected?.kind === 'section'
    ? sectionMeta(selected.id).name
    : selected?.kind === 'theme'
      ? THEME_CATS.find((category) => category.id === selected.id)?.name
      : '';

  return (
    <div className="ed">
      <header className="ed-topbar">
        <div className="ed-topbar-left">
          <a className="ed-iconbtn" href="/admin" aria-label="Exit theme editor"><ArrowLeft size={18} /></a>
          <span className="ed-divider" />
          <EditorNavButton label="Sections" active={mode === 'sections'} onClick={() => toggleMode('sections')}><PanelLeft size={19} /></EditorNavButton>
          <EditorNavButton label="Theme settings" active={mode === 'theme'} onClick={() => toggleMode('theme')}><Settings2 size={19} /></EditorNavButton>
          <EditorNavButton label="App embeds" active={mode === 'embeds'} onClick={() => toggleMode('embeds')}><Blocks size={19} /></EditorNavButton>
        </div>

        <div className="ed-topbar-center">
          <TopbarMenuButton id="theme" open={menu === 'theme'} onToggle={() => setMenu(menu === 'theme' ? null : 'theme')} icon={<Store size={17} />} label="Sugarloaf" badge="Live">
            <div className="ed-menu-title">Sugarloaf theme</div>
            <a href="/" target="_blank" rel="noreferrer"><ExternalLink size={16} />View live site</a>
            <a href="/admin"><ArrowLeft size={16} />Return to admin</a>
            <button type="button"><Keyboard size={16} />Keyboard shortcuts<span className="ed-menu-shortcut">⌘ /</span></button>
          </TopbarMenuButton>
          <TopbarMenuButton id="audience" open={menu === 'audience'} onToggle={() => setMenu(menu === 'audience' ? null : 'audience')} icon={<Globe2 size={17} />} label="Site default">
            <div className="ed-menu-title">Audience</div>
            <button type="button" className="is-selected"><Globe2 size={16} /><span><b>Site default</b><small>All visitors</small></span><Check size={16} /></button>
            <p className="ed-menu-note">Your current site is tailored to families in Maryland and the Mid-Atlantic.</p>
          </TopbarMenuButton>
          <TopbarMenuButton id="template" open={menu === 'template'} onToggle={() => setMenu(menu === 'template' ? null : 'template')} icon={<Home size={17} />} label="Home page">
            <div className="ed-menu-title">Templates</div>
            <button type="button" className="is-selected"><Home size={16} />Home page<Check size={16} /></button>
            <a href="/apply" target="_blank" rel="noreferrer"><ExternalLink size={16} />Preview puppy application</a>
          </TopbarMenuButton>
        </div>

        <div className="ed-topbar-right">
          <div className="ed-menu-anchor ed-assistant-anchor">
            <button type="button" className="ed-iconbtn" aria-label="Sugarloaf shortcuts" aria-expanded={menu === 'assistant'} onClick={() => setMenu(menu === 'assistant' ? null : 'assistant')}><Sparkles size={18} /></button>
            {menu === 'assistant' && (
              <div className="ed-popover ed-popover-right ed-assistant-menu">
                <div className="ed-menu-title">Breeder shortcuts</div>
                <a href="/admin/applications"><ClipboardCheck size={16} />Review applications</a>
                <a href="/admin/litters"><Calendar size={16} />Manage litters</a>
                <a href="/admin/newsletter"><Mail size={16} />Email families</a>
              </div>
            )}
          </div>
          <button type="button" className={`ed-iconbtn ${inspector ? 'on' : ''}`} aria-label="Preview inspector" aria-pressed={inspector} onClick={() => setInspector((value) => !value)}><MousePointer2 size={18} /></button>
          <button type="button" className={`ed-iconbtn ${device === 'mobile' ? 'on' : ''}`} aria-label={device === 'mobile' ? 'Show desktop preview' : 'Show mobile preview'} aria-pressed={device === 'mobile'} onClick={() => setDevice((value) => value === 'desktop' ? 'mobile' : 'desktop')}>
            {device === 'mobile' ? <Monitor size={18} /> : <Smartphone size={18} />}
          </button>
          <div className="ed-undo-group">
            <button type="button" className="ed-iconbtn" aria-label="Undo" disabled={!canUndo} onClick={undo}><Undo2 size={18} /></button>
            <button type="button" className="ed-iconbtn" aria-label="Redo" disabled={!canRedo} onClick={redo}><Redo2 size={18} /></button>
          </div>
          <a className="ed-preview-link" href="/" target="_blank" rel="noreferrer">View</a>
          <span className="ed-save-wrap">
            {editorDirty && <span className="ed-dirty-dot" title="Unsaved changes" />}
            <button type="button" className="ed-save" disabled={!editorDirty || saving} onClick={() => void onSave()}>{saving ? 'Saving…' : 'Save'}</button>
          </span>
        </div>
      </header>

      <div className={`ed-body ${mode ? '' : 'sidebar-collapsed'} ${settingsOpen ? 'has-settings' : ''}`}>
        {mode && (
          <aside className="ed-left" aria-label={mode === 'sections' ? 'Page sections' : mode === 'theme' ? 'Theme settings' : 'App embeds'}>
            {mode === 'sections' && (
              <>
                <div className="ed-panel-title"><span>Home page</span><button type="button" aria-label="Page actions"><span>•••</span></button></div>
                {(['header', 'template', 'footer'] as SectionGroup[]).map((group) => {
                  const items = EDITOR_SECTIONS
                    .filter((section) => section.group === group)
                    .sort((a, b) => content.sections.order.indexOf(a.id) - content.sections.order.indexOf(b.id));
                  return (
                    <div className="ed-section-group" key={group}>
                      <button type="button" className="ed-group-head" onClick={() => setCollapsed((value) => ({ ...value, [group]: !value[group] }))} aria-expanded={!collapsed[group]}>
                        {collapsed[group] ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                        <span>{group === 'header' ? 'Header' : group === 'template' ? 'Template' : 'Footer'}</span>
                      </button>
                      {!collapsed[group] && items.map((section) => {
                        const Icon = section.icon;
                        const hidden = section.id === 'facebook' ? !isFacebookSectionVisible(content) : content.sections.hidden.includes(section.id);
                        const draggable = group === 'template';
                        return (
                          <button
                            type="button"
                            key={section.id}
                            draggable={draggable}
                            className={`ed-row ${selected?.kind === 'section' && selected.id === section.id ? 'on' : ''} ${hidden ? 'is-hidden' : ''} ${dragging === section.id ? 'is-dragging' : ''}`}
                            onClick={() => selectSection(section.id)}
                            onDragStart={(event) => { setDragging(section.id); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', section.id); }}
                            onDragEnd={() => setDragging(null)}
                            onDragOver={(event) => { if (draggable) event.preventDefault(); }}
                            onDrop={(event) => { event.preventDefault(); if (dragging) moveSection(dragging, section.id); setDragging(null); }}
                          >
                            <span className="ed-row-grip" aria-hidden>{draggable ? <GripVertical size={15} /> : <span />}</span>
                            <Icon className="ed-row-icon" size={17} />
                            <span className="ed-row-name">{section.name}</span>
                            <span
                              role="button"
                              tabIndex={0}
                              className="ed-row-eye"
                              aria-label={hidden ? `Show ${section.name}` : `Hide ${section.name}`}
                              onClick={(event) => { event.stopPropagation(); toggleHidden(section.id); }}
                              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); toggleHidden(section.id); } }}
                            >
                              {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                            </span>
                          </button>
                        );
                      })}
                      {group === 'template' && (
                        <div className="ed-add-wrap">
                          <button type="button" className="ed-add-section" onClick={() => setAddOpen((value) => !value)}><Plus size={16} />Add section</button>
                          {addOpen && (
                            <div className="ed-add-menu">
                              <strong>Add section</strong>
                              {hiddenTemplateSections.length ? hiddenTemplateSections.map((section) => {
                                const Icon = section.icon;
                                return <button type="button" key={section.id} onClick={() => { toggleHidden(section.id); selectSection(section.id); setAddOpen(false); }}><Icon size={17} />{section.name}</button>;
                              }) : <p>All available sections are already on this page.</p>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {mode === 'theme' && (
              <>
                <div className="ed-panel-title"><span>Theme settings</span></div>
                <p className="ed-panel-intro">Set the visual foundation for every section.</p>
                {THEME_CATS.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button key={category.id} type="button" className={`ed-theme-row ${selected?.kind === 'theme' && selected.id === category.id ? 'on' : ''}`} onClick={() => selectThemeSetting(category.id)}>
                      <span className="ed-theme-row-icon"><Icon size={18} /></span>
                      <span><b>{category.name}</b><small>{category.description}</small></span>
                      <ChevronRight size={16} />
                    </button>
                  );
                })}
              </>
            )}

            {mode === 'embeds' && (
              <>
                <div className="ed-panel-title"><span>App embeds</span></div>
                <p className="ed-panel-intro">Turn Sugarloaf-specific storefront helpers on or off.</p>
                <EmbedToggle icon={Calendar} title="Litter availability spotlight" description="Shows the accepting-applications card in the hero when a litter has open spots." checked={content.features.availabilityCard} onChange={(checked) => setContent((current) => ({ ...current, features: { ...current.features, availabilityCard: checked } }))} />
                <EmbedToggle icon={ShieldCheck} title="Health trust bar" description="Displays health and membership assurances below the hero." checked={content.features.trustBar} onChange={(checked) => setContent((current) => ({ ...current, features: { ...current.features, trustBar: checked } }))} />
                <EmbedToggle icon={Mail} title="Litter announcement signup" description="Lets families join the announcement list from the footer." checked={content.features.newsletterSignup} onChange={(checked) => setContent((current) => ({ ...current, features: { ...current.features, newsletterSignup: checked } }))} />
                <EmbedToggle icon={Video} title="Facebook videos" description="Shows the editable Facebook video and post section on the home page." checked={isFacebookSectionVisible(content)} onChange={(checked) => setContent((current) => setFacebookSectionVisibility(current, checked))} />
              </>
            )}
          </aside>
        )}

        <main className={`ed-preview ${device}`} ref={paneRef}>
          <div className="ed-preview-stage" style={{ width: deviceWidth * scale }}>
            <iframe
              ref={iframeRef}
              className="ed-iframe"
              title="Live site preview"
              src="/?__ed=1"
              style={{ width: deviceWidth, height: `calc((100vh - 104px) / ${scale})`, transform: `scale(${scale})`, transformOrigin: 'top left' }}
            />
          </div>
          {device === 'mobile' && <span className="ed-device-label">390 px</span>}
        </main>

        {settingsOpen && (
          <aside id="editor-settings-panel" className="ed-right" aria-label={selected ? `${selectedTitle} settings` : 'Editor settings'}>
            <div className="ed-right-handle" aria-hidden><span /></div>
            <div className="ed-right-head">
              <button type="button" className="ed-iconbtn sm ed-panel-back" aria-label="Back to section list" disabled={!selected} onClick={() => { setSelected(null); setSettingsOpen(false); }}><ChevronLeft size={18} /></button>
              <span>{selectedTitle || 'Settings'}</span>
              <button type="button" className="ed-iconbtn sm" aria-label="Close settings panel" onClick={() => setSettingsOpen(false)}><X size={16} /></button>
            </div>
            <div className="ed-right-body" ref={rightBodyRef}>
              {selected ? (
                <SettingsFor selected={selected} content={content} setContent={setContent} litters={litters} setLitters={setLitters} openPicker={openPicker} openCrop={openCrop} />
              ) : (
                <div className="ed-right-empty">
                  <span className="ed-right-empty-icon"><Settings2 size={20} /></span>
                  <strong>Select something to edit</strong>
                  <p>Choose a page section or a theme setting to see its controls here.</p>
                  <div>
                    <button type="button" className="ed-secondary-action" onClick={() => setMode('sections')}>Browse sections</button>
                    <button type="button" className="ed-secondary-action" onClick={() => setMode('theme')}>Theme settings</button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {(saved || saveError) && <div className={`ed-toast ${saveError ? 'is-error' : ''}`} role="status">{saveError || 'Changes saved'}</div>}
      <MediaModal open={Boolean(picker)} dogId={picker?.dogId} onClose={() => setPicker(null)} onSelect={(url) => picker?.apply(url)} />
      <ImageCropModal
        open={Boolean(cropper)}
        src={cropper?.src ?? ''}
        label={cropper?.label ?? 'Image'}
        aspectRatio={cropper?.aspectRatio ?? 1}
        value={cropper?.value}
        defaultValue={cropper?.defaultValue}
        onClose={() => setCropper(null)}
        onApply={(crop) => cropper?.apply(crop)}
      />
    </div>
  );
}

function EditorNavButton({ label, active, onClick, children }: { label: string; active: boolean; onClick: () => void; children: ReactNode }) {
  return <button type="button" className={`ed-iconbtn ${active ? 'on' : ''}`} aria-label={label} aria-pressed={active} onClick={onClick}>{children}</button>;
}

function TopbarMenuButton({ id, open, onToggle, icon, label, badge, children }: { id: string; open: boolean; onToggle: () => void; icon: ReactNode; label: string; badge?: string; children: ReactNode }) {
  return (
    <div className="ed-menu-anchor">
      <button type="button" className="ed-context-btn" aria-expanded={open} aria-controls={`ed-menu-${id}`} onClick={onToggle}>
        {icon}<span>{label}</span>{badge && <b>{badge}</b>}<ChevronDown size={14} />
      </button>
      {open && <div className="ed-popover" id={`ed-menu-${id}`}>{children}</div>}
    </div>
  );
}

function EmbedToggle({ icon: Icon, title, description, checked, onChange }: { icon: LucideIcon; title: string; description: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="ed-embed-row">
      <span className="ed-embed-icon"><Icon size={18} /></span>
      <span><b>{title}</b><small>{description}</small></span>
      <Switch checked={checked} label={title} onChange={onChange} />
    </div>
  );
}

type SettingsProps = {
  selected: Exclude<Selected, null>;
  content: SiteContent;
  setContent: (updater: (content: SiteContent) => SiteContent) => void;
  litters: Litter[];
  setLitters: (updater: (litters: Litter[]) => Litter[]) => void;
  openPicker: (apply: (url: string) => void, dogId?: string) => void;
  openCrop: (options: CropEditorState) => void;
};

function SettingsFor({ selected, content, setContent, litters, setLitters, openPicker, openCrop }: SettingsProps) {
  const setHero = (patch: Partial<SiteContent['hero']>) => setContent((current) => ({ ...current, hero: { ...current.hero, ...patch } }));
  const setCopy = (key: keyof SiteContent['copy'], value: string) => setContent((current) => ({ ...current, copy: { ...current.copy, [key]: value } }));
  const updateDog = (id: string, update: (dog: SiteContent['lineage'][number]) => SiteContent['lineage'][number]) => setContent((current) => ({ ...current, lineage: current.lineage.map((dog) => dog.id === id ? update(dog) : dog) }));
  const missingNamedDogs = sugarloafPlaceholderDogs.filter((template) => !content.lineage.some((dog) => dog.id === template.id || dog.name.trim().toLowerCase() === template.name.toLowerCase()));
  const addDog = (template?: LineageDog) => setContent((current) => {
    const profile = template
      ? { ...copyDogProfile(template), id: uniqueDogProfileId(template.id || template.name, current.lineage) }
      : newDogProfile(current.lineage);
    return { ...current, lineage: [...current.lineage, profile] };
  });
  const addMissingNamedDogs = () => setContent((current) => {
    const additions = sugarloafPlaceholderDogs
      .filter((template) => !current.lineage.some((dog) => dog.id === template.id || dog.name.trim().toLowerCase() === template.name.toLowerCase()))
      .map((template) => ({ ...copyDogProfile(template), id: uniqueDogProfileId(template.id || template.name, current.lineage) }));
    return additions.length ? { ...current, lineage: [...current.lineage, ...additions] } : current;
  });
  const moveDog = (id: string, offset: -1 | 1) => setContent((current) => {
    const index = current.lineage.findIndex((dog) => dog.id === id);
    const destination = index + offset;
    if (index < 0 || destination < 0 || destination >= current.lineage.length) return current;
    const lineage = [...current.lineage];
    [lineage[index], lineage[destination]] = [lineage[destination], lineage[index]];
    return { ...current, lineage };
  });
  const replaceHeroPhoto = (index: number, src: string) => {
    setContent((current) => ({
      ...current,
      heroPhotos: current.heroPhotos.map((photo, photoIndex) => photoIndex === index ? src : photo),
      heroPhotoCrops: current.heroPhotos.map((_, photoIndex) => photoIndex === index ? null : (current.heroPhotoCrops?.[photoIndex] ?? null)),
    }));
    openCrop({
      src,
      label: `Hero photo ${index + 1}`,
      aspectRatio: 4 / 3,
      value: null,
      defaultValue: HERO_CROP_DEFAULT,
      apply: (crop) => setContent((current) => ({
        ...current,
        heroPhotoCrops: current.heroPhotos.map((_, photoIndex) => photoIndex === index ? crop : (current.heroPhotoCrops?.[photoIndex] ?? null)),
      })),
    });
  };
  const removeHeroPhoto = (index: number) => setContent((current) => ({
    ...current,
    heroPhotos: current.heroPhotos.filter((_, photoIndex) => photoIndex !== index),
    heroPhotoCrops: current.heroPhotos.filter((_, photoIndex) => photoIndex !== index).map((_, photoIndex) => {
      const originalIndex = photoIndex >= index ? photoIndex + 1 : photoIndex;
      return current.heroPhotoCrops?.[originalIndex] ?? null;
    }),
  }));
  const addHeroPhoto = (src: string) => {
    const index = content.heroPhotos.length;
    setContent((current) => ({ ...current, heroPhotos: [...current.heroPhotos, src], heroPhotoCrops: [...current.heroPhotos.map((_, photoIndex) => current.heroPhotoCrops?.[photoIndex] ?? null), null] }));
    openCrop({
      src,
      label: `Hero photo ${index + 1}`,
      aspectRatio: 4 / 3,
      value: null,
      defaultValue: HERO_CROP_DEFAULT,
      apply: (crop) => setContent((current) => ({ ...current, heroPhotoCrops: current.heroPhotos.map((_, photoIndex) => photoIndex === index ? crop : (current.heroPhotoCrops?.[photoIndex] ?? null)) })),
    });
  };
  const setGallery = (gallery: SiteContent['gallery']) => setContent((current) => ({ ...current, gallery }));
  const updateGallery = (index: number, update: (item: SiteContent['gallery'][number]) => SiteContent['gallery'][number]) => setContent((current) => ({ ...current, gallery: current.gallery.map((item, itemIndex) => itemIndex === index ? update(item) : item) }));
  const updateFacebookItem = (id: string, values: Partial<SiteContent['facebook']['items'][number]>) => setContent((current) => ({
    ...current,
    facebook: { ...current.facebook, items: current.facebook.items.map((item) => item.id === id ? { ...item, ...values } : item) },
  }));
  const moveFacebookItem = (id: string, offset: -1 | 1) => setContent((current) => {
    const index = current.facebook.items.findIndex((item) => item.id === id);
    const destination = index + offset;
    if (index < 0 || destination < 0 || destination >= current.facebook.items.length) return current;
    const items = [...current.facebook.items];
    [items[index], items[destination]] = [items[destination], items[index]];
    return { ...current, facebook: { ...current.facebook, items } };
  });
  const addFacebookItem = (kind: 'video' | 'post') => setContent((current) => setFacebookSectionVisibility({
    ...current,
    facebook: {
      ...current.facebook,
      items: [...current.facebook.items, { id: crypto.randomUUID(), kind, url: '', title: kind === 'video' ? 'New Facebook video' : 'New Facebook post' }],
    },
  }, true));

  if (selected.kind === 'theme') {
    if (selected.id === 'colors') return (
      <>
        <InspectorHeading title="Brand colors" description="Changes update the live preview instantly." />
        {THEME_COLORS.map((field) => <FieldColor key={field.key} label={field.label} hint={field.hint} value={content.theme.colors[field.key]} onChange={(value) => setContent((current) => ({ ...current, theme: { ...current.theme, colors: { ...current.theme.colors, [field.key]: value } } }))} />)}
        <button type="button" className="ed-secondary-action" onClick={() => setContent((current) => ({ ...current, theme: { ...current.theme, colors: defaultSiteContent.theme.colors } }))}>Reset colors</button>
      </>
    );

    if (selected.id === 'typography') return (
      <>
        <InspectorHeading title="Typography" description="Choose a clear, readable type pairing for the storefront." />
        <Setting label="Headings">
          <select value={content.theme.typography.heading} onChange={(event) => setContent((current) => ({ ...current, theme: { ...current.theme, typography: { ...current.theme.typography, heading: event.target.value as ThemeTypography['heading'] } } }))}>
            <option value="bricolage">Bricolage Grotesque</option>
            <option value="serif">Editorial serif</option>
            <option value="system">System sans</option>
          </select>
        </Setting>
        <Setting label="Body text">
          <select value={content.theme.typography.body} onChange={(event) => setContent((current) => ({ ...current, theme: { ...current.theme, typography: { ...current.theme.typography, body: event.target.value as ThemeTypography['body'] } } }))}>
            <option value="nunito">Nunito Sans</option>
            <option value="system">System sans</option>
            <option value="serif">Editorial serif</option>
          </select>
        </Setting>
        <div className="ed-type-preview"><strong>Raised with intention.</strong><p>Healthy, family-raised golden retrievers from Urbana, Maryland.</p></div>
      </>
    );

    if (selected.id === 'layout') return (
      <>
        <InspectorHeading title="Layout" description="Control the rhythm and density of the public site." />
        <Setting label="Content width">
          <select value={content.theme.layout.contentWidth} onChange={(event) => updateThemeLayout(setContent, { contentWidth: Number(event.target.value) as ThemeLayout['contentWidth'] })}>
            <option value={1120}>Narrow</option><option value={1240}>Standard</option><option value={1360}>Wide</option>
          </select>
        </Setting>
        <Setting label="Section spacing">
          <select value={content.theme.layout.sectionSpacing} onChange={(event) => updateThemeLayout(setContent, { sectionSpacing: Number(event.target.value) as ThemeLayout['sectionSpacing'] })}>
            <option value={36}>Compact</option><option value={48}>Comfortable</option><option value={64}>Generous</option>
          </select>
        </Setting>
        <Setting label="Corner style">
          <select value={content.theme.layout.cornerRadius} onChange={(event) => updateThemeLayout(setContent, { cornerRadius: Number(event.target.value) as ThemeLayout['cornerRadius'] })}>
            <option value={12}>Subtle</option><option value={18}>Soft</option><option value={24}>Rounded</option>
          </select>
        </Setting>
      </>
    );

    return (
      <>
        <InspectorHeading title="Buttons" description="Keep application and contact actions consistent." />
        <Setting label="Shape">
          <ChoiceGroup value={content.theme.buttons.shape} options={[['pill', 'Pill'], ['rounded', 'Rounded'], ['square', 'Square']]} onChange={(value) => updateThemeButtons(setContent, { shape: value as ThemeButtons['shape'] })} />
        </Setting>
        <Setting label="Primary color">
          <ChoiceGroup value={content.theme.buttons.tone} options={[['ink', 'Dark'], ['accent', 'Accent']]} onChange={(value) => updateThemeButtons(setContent, { tone: value as ThemeButtons['tone'] })} />
        </Setting>
        <div className="ed-button-preview" style={{ borderRadius: content.theme.buttons.shape === 'pill' ? 999 : content.theme.buttons.shape === 'rounded' ? 12 : 3, background: content.theme.buttons.tone === 'accent' ? content.theme.colors.orange : content.theme.colors.ink }}>Apply for a puppy</div>
      </>
    );
  }

  if (selected.id === 'hero') return (
    <>
      <InspectorHeading title="Hero" description="The first message families see when they visit." />
      <FieldText label="Eyebrow" value={content.hero.eyebrow} onChange={(value) => setHero({ eyebrow: value })} />
      <FieldText label="Heading" field="heading" value={content.hero.title} onChange={(value) => setHero({ title: value })} area />
      <FieldText label="Subtitle" field="subtitle" value={content.hero.subtitle} onChange={(value) => setHero({ subtitle: value })} area />
      <div className="ed-field-grid">
        <FieldText label="Primary button" value={content.hero.ctaPrimary} onChange={(value) => setHero({ ctaPrimary: value })} />
        <FieldText label="Secondary button" value={content.hero.ctaSecondary} onChange={(value) => setHero({ ctaSecondary: value })} />
      </div>
      <FieldList label="Trust statements" hint="One statement per line" value={content.hero.trust} onChange={(value) => setHero({ trust: value })} />
      <Setting label="Film-strip photos" hint="Replace an image or adjust its crop for this placement.">
        <div className="ed-gallery-grid">
          {content.heroPhotos.map((src, index) => (
            <div key={`${src}-${index}`} className="ed-gallery-cell">
              <button type="button" aria-label={`Replace hero photo ${index + 1}`} onClick={() => openPicker((url) => replaceHeroPhoto(index, url))}><img src={src} alt="" style={imageCropStyle(content.heroPhotoCrops?.[index])} /></button>
              <button
                type="button"
                className={`ed-gallery-crop ${content.heroPhotoCrops?.[index] ? 'is-custom' : ''}`}
                aria-label={`Adjust crop for hero photo ${index + 1}`}
                onClick={() => openCrop({
                  src,
                  label: `Hero photo ${index + 1}`,
                  aspectRatio: 4 / 3,
                  value: content.heroPhotoCrops?.[index],
                  defaultValue: HERO_CROP_DEFAULT,
                  apply: (crop) => setContent((current) => ({ ...current, heroPhotoCrops: current.heroPhotos.map((_, photoIndex) => photoIndex === index ? crop : (current.heroPhotoCrops?.[photoIndex] ?? null)) })),
                })}
              ><Crop size={12} /></button>
              <button type="button" className="ed-gallery-x" aria-label={`Remove hero photo ${index + 1}`} onClick={() => removeHeroPhoto(index)}><X size={12} /></button>
            </div>
          ))}
        </div>
        <button type="button" className="ed-add-block" onClick={() => openPicker(addHeroPhoto)}><Plus size={16} />Add photo</button>
      </Setting>
    </>
  );

  if (selected.id === 'dogs') return (
    <>
      <InspectorHeading title="Our dogs" description="Present breeding dogs with clearances and accomplishments." />
      <FieldText label="Kicker" field="dogsKicker" value={content.copy.dogsKicker} onChange={(value) => setCopy('dogsKicker', value)} />
      <FieldText label="Title" field="dogsTitle" value={content.copy.dogsTitle} onChange={(value) => setCopy('dogsTitle', value)} />
      <FieldText label="Introduction" field="dogsSubtext" value={content.copy.dogsSubtext} onChange={(value) => setCopy('dogsSubtext', value)} area />
      <BlockDivider label="Dogs" />
      {missingNamedDogs.length > 0 && (
        <div className="ed-dog-presets">
          <span>Profiles ready to add</span>
          <div>{missingNamedDogs.map((dog) => <button type="button" key={dog.id} onClick={() => addDog(dog)}><Plus size={13} />{dog.name}</button>)}</div>
          {missingNamedDogs.length > 1 && <button type="button" className="ed-text-action" onClick={addMissingNamedDogs}>Add all missing profiles</button>}
        </div>
      )}
      {content.lineage.map((dog, index) => (
        <EditorBlock key={dog.id} title={dog.name || 'Dog'} subtitle={dog.title} icon={<Dog size={16} />}>
          <FieldImage
            label="Photo"
            src={dog.image.src}
            alt={dog.image.alt}
            crop={dog.image.crop}
            onPick={() => openPicker((url) => {
              updateDog(dog.id, (current) => ({ ...current, image: { ...current.image, src: url, crop: undefined } }));
              openCrop({
                src: url,
                label: `${dog.name || 'Dog'} photo`,
                aspectRatio: 4 / 3,
                value: null,
                defaultValue: DOG_CROP_DEFAULT,
                apply: (crop) => updateDog(dog.id, (current) => ({ ...current, image: { ...current.image, crop: crop ?? undefined } })),
              });
            }, dog.id)}
            onCrop={() => openCrop({
              src: dog.image.src,
              label: `${dog.name || 'Dog'} photo`,
              aspectRatio: 4 / 3,
              value: dog.image.crop,
              defaultValue: DOG_CROP_DEFAULT,
              apply: (crop) => updateDog(dog.id, (current) => ({ ...current, image: { ...current.image, crop: crop ?? undefined } })),
            })}
            onRemove={() => updateDog(dog.id, (current) => ({ ...current, image: { ...current.image, src: '', crop: undefined } }))}
          />
          <FieldText label="Photo alt text" value={dog.image.alt} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, image: { ...current.image, alt: value } }))} />
          <FieldText label="Name" value={dog.name} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, name: value }))} />
          <FieldText label="Registered name" value={dog.registeredName} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, registeredName: value }))} />
          <FieldText label="Title / role" value={dog.title} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, title: value }))} />
          <FieldText label="Description" value={dog.description} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, description: value }))} area />
          <FieldList label="Health clearances" value={dog.health} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, health: value }))} />
          <FieldList label="Titles & awards" value={dog.awards} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, awards: value }))} />
          <FieldText label="Photo display filter" value={dog.filter ?? ''} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, filter: value || undefined }))} />
          <Setting label="Pedigree details" hint="Show sire, dam, and grandparents in the dog profile.">
            <Switch checked={Boolean(dog.pedigree)} label={`Pedigree details for ${dog.name || 'dog'}`} onChange={(checked) => updateDog(dog.id, (current) => ({ ...current, pedigree: checked ? (current.pedigree ?? { sire: '', sireGrands: [], dam: '', damGrands: [] }) : undefined }))} />
          </Setting>
          {dog.pedigree && (
            <>
              <FieldText label="Sire" value={dog.pedigree.sire} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, pedigree: { ...(current.pedigree ?? { sire: '', sireGrands: [], dam: '', damGrands: [] }), sire: value } }))} />
              <FieldList label="Sire’s parents" value={dog.pedigree.sireGrands} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, pedigree: { ...(current.pedigree ?? { sire: '', sireGrands: [], dam: '', damGrands: [] }), sireGrands: value } }))} />
              <FieldText label="Dam" value={dog.pedigree.dam} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, pedigree: { ...(current.pedigree ?? { sire: '', sireGrands: [], dam: '', damGrands: [] }), dam: value } }))} />
              <FieldList label="Dam’s parents" value={dog.pedigree.damGrands} onChange={(value) => updateDog(dog.id, (current) => ({ ...current, pedigree: { ...(current.pedigree ?? { sire: '', sireGrands: [], dam: '', damGrands: [] }), damGrands: value } }))} />
            </>
          )}
          <div className="ed-dog-actions">
            <button type="button" className="ed-secondary-action" disabled={index === 0} onClick={() => moveDog(dog.id, -1)}>Move up</button>
            <button type="button" className="ed-secondary-action" disabled={index === content.lineage.length - 1} onClick={() => moveDog(dog.id, 1)}>Move down</button>
          </div>
          {content.lineage.length > 1 && <RemoveButton label={`Remove ${dog.name || 'dog'}`} onClick={() => setContent((current) => ({ ...current, lineage: current.lineage.filter((item) => item.id !== dog.id) }))} />}
        </EditorBlock>
      ))}
      <AddBlockButton label="Add custom dog" onClick={() => addDog()} />
    </>
  );

  if (selected.id === 'health') return (
    <>
      <InspectorHeading title="Health standards" description="Explain the clearances behind every pairing." />
      <FieldText label="Kicker" value={content.copy.healthKicker} onChange={(value) => setCopy('healthKicker', value)} />
      <FieldText label="Title" value={content.copy.healthTitle} onChange={(value) => setCopy('healthTitle', value)} />
      <FieldText label="Introduction" value={content.copy.healthIntro} onChange={(value) => setCopy('healthIntro', value)} area />
      <BlockDivider label="Standards" />
      {content.healthStandards.map((standard, index) => (
        <EditorBlock key={`${standard.title}-${index}`} title={standard.title || 'Health standard'} icon={<ShieldCheck size={16} />}>
          <FieldText label="Code" value={standard.code} onChange={(value) => setContent((current) => ({ ...current, healthStandards: current.healthStandards.map((item, itemIndex) => itemIndex === index ? { ...item, code: value } : item) }))} />
          <FieldText label="Title" value={standard.title} onChange={(value) => setContent((current) => ({ ...current, healthStandards: current.healthStandards.map((item, itemIndex) => itemIndex === index ? { ...item, title: value } : item) }))} />
          <FieldText label="Body" value={standard.body} onChange={(value) => setContent((current) => ({ ...current, healthStandards: current.healthStandards.map((item, itemIndex) => itemIndex === index ? { ...item, body: value } : item) }))} area />
          <RemoveButton label="Remove standard" onClick={() => setContent((current) => ({ ...current, healthStandards: current.healthStandards.filter((_, itemIndex) => itemIndex !== index) }))} />
        </EditorBlock>
      ))}
      <AddBlockButton label="Add health standard" onClick={() => setContent((current) => ({ ...current, healthStandards: [...current.healthStandards, { code: '', title: 'New standard', body: '' }] }))} />
    </>
  );

  if (selected.id === 'weeks') return (
    <>
      <InspectorHeading title="First eight weeks" description="Show families how puppies are raised before gotcha day." />
      <FieldText label="Kicker" value={content.copy.weeksKicker} onChange={(value) => setCopy('weeksKicker', value)} />
      <FieldText label="Title" value={content.copy.weeksTitle} onChange={(value) => setCopy('weeksTitle', value)} />
      <FieldText label="Introduction" value={content.copy.weeksSubtext} onChange={(value) => setCopy('weeksSubtext', value)} area />
      <BlockDivider label="Milestones" />
      {content.weeks.map((week, index) => (
        <EditorBlock key={`${week.week}-${index}`} title={week.week || `Milestone ${index + 1}`} subtitle={week.title} icon={<CalendarDays size={16} />}>
          <FieldText label="Week label" value={week.week} onChange={(value) => setContent((current) => ({ ...current, weeks: current.weeks.map((item, itemIndex) => itemIndex === index ? { ...item, week: value } : item) }))} />
          <FieldText label="Title" value={week.title} onChange={(value) => setContent((current) => ({ ...current, weeks: current.weeks.map((item, itemIndex) => itemIndex === index ? { ...item, title: value } : item) }))} />
          <FieldText label="Body" value={week.body} onChange={(value) => setContent((current) => ({ ...current, weeks: current.weeks.map((item, itemIndex) => itemIndex === index ? { ...item, body: value } : item) }))} area />
          {content.weeks.length > 1 && <RemoveButton label="Remove milestone" onClick={() => setContent((current) => ({ ...current, weeks: current.weeks.filter((_, itemIndex) => itemIndex !== index) }))} />}
        </EditorBlock>
      ))}
      <AddBlockButton label="Add milestone" onClick={() => setContent((current) => ({ ...current, weeks: [...current.weeks, { week: 'NEW WEEK', title: 'New milestone', body: '' }] }))} />
    </>
  );

  if (selected.id === 'gallery') return (
    <>
      <InspectorHeading title="Gallery" description="Share puppy, adult dog, and adventure photos." />
      <FieldText label="Kicker" field="galleryKicker" value={content.copy.galleryKicker} onChange={(value) => setCopy('galleryKicker', value)} />
      <FieldText label="Title" field="galleryTitle" value={content.copy.galleryTitle} onChange={(value) => setCopy('galleryTitle', value)} />
      <BlockDivider label="Photos" />
      {content.gallery.map((item, index) => (
        <EditorBlock key={`${item.src}-${index}`} title={item.caption || `Photo ${index + 1}`} subtitle={item.tag} icon={<ImageIcon size={16} />}>
          <FieldImage
            label="Photo"
            src={item.src}
            alt={item.caption}
            crop={item.crop}
            onPick={() => openPicker((url) => {
              updateGallery(index, (current) => ({ ...current, src: url, crop: undefined }));
              openCrop({
                src: url,
                label: item.caption || `Gallery photo ${index + 1}`,
                aspectRatio: item.span === 2 ? 2 / 3 : 4 / 3,
                value: null,
                defaultValue: GALLERY_CROP_DEFAULT,
                apply: (crop) => updateGallery(index, (current) => ({ ...current, crop: crop ?? undefined })),
              });
            })}
            onCrop={() => openCrop({
              src: item.src,
              label: item.caption || `Gallery photo ${index + 1}`,
              aspectRatio: item.span === 2 ? 2 / 3 : 4 / 3,
              value: item.crop,
              defaultValue: GALLERY_CROP_DEFAULT,
              apply: (crop) => updateGallery(index, (current) => ({ ...current, crop: crop ?? undefined })),
            })}
            onRemove={() => updateGallery(index, (current) => ({ ...current, src: '', crop: undefined }))}
          />
          <FieldText label="Caption" value={item.caption} onChange={(value) => updateGallery(index, (current) => ({ ...current, caption: value }))} />
          <Setting label="Category"><select value={item.tag} onChange={(event) => updateGallery(index, (current) => ({ ...current, tag: event.target.value }))}>{['Puppies', 'Adults', 'Adventures'].map((tag) => <option key={tag} value={tag}>{tag}</option>)}</select></Setting>
          <RemoveButton label="Remove photo" onClick={() => setGallery(content.gallery.filter((_, itemIndex) => itemIndex !== index))} />
        </EditorBlock>
      ))}
      <AddBlockButton label="Add gallery photo" onClick={() => openPicker((url) => {
        const index = content.gallery.length;
        setGallery([...content.gallery, { src: url, caption: 'New golden moment', tag: 'Puppies', span: 1 }]);
        openCrop({
          src: url,
          label: `Gallery photo ${index + 1}`,
          aspectRatio: 4 / 3,
          value: null,
          defaultValue: GALLERY_CROP_DEFAULT,
          apply: (crop) => updateGallery(index, (current) => ({ ...current, crop: crop ?? undefined })),
        });
      })} />
    </>
  );

  if (selected.id === 'facebook') return (
    <>
      <InspectorHeading title="Facebook videos" description="Feature public videos, reels, and posts from Sugarloaf’s exact Facebook page." />
      <Setting label="Show on public site" hint="You can also toggle this from App embeds.">
        <Switch checked={isFacebookSectionVisible(content)} label="Show Facebook section" onChange={(enabled) => setContent((current) => setFacebookSectionVisibility(current, enabled))} />
      </Setting>
      <FieldFacebookUrl label="Facebook page URL" field="pageUrl" value={content.facebook.pageUrl} onChange={(pageUrl) => setContent((current) => ({ ...current, facebook: { ...current.facebook, pageUrl } }))} />
      <div className="ed-info-card"><Video size={18} /><span><b>Paste each public permalink</b><small>Open a video, reel, or post on Facebook, copy its full URL, and paste it below. The page link remains the fallback.</small></span></div>
      <FieldText label="Eyebrow" field="eyebrow" value={content.facebook.eyebrow} onChange={(eyebrow) => setContent((current) => ({ ...current, facebook: { ...current.facebook, eyebrow } }))} />
      <FieldText label="Title" field="title" value={content.facebook.title} onChange={(title) => setContent((current) => ({ ...current, facebook: { ...current.facebook, title } }))} />
      <FieldText label="Introduction" field="intro" value={content.facebook.intro} onChange={(intro) => setContent((current) => ({ ...current, facebook: { ...current.facebook, intro } }))} area />
      <BlockDivider label="Featured updates" />
      {content.facebook.items.map((item, index) => (
        <EditorBlock key={item.id} title={item.title || `Facebook ${item.kind}`} subtitle={item.kind === 'video' ? 'Video or reel' : 'Post'} icon={<Video size={16} />}>
          <Setting label="Embed type">
            <select value={item.kind} onChange={(event) => updateFacebookItem(item.id, { kind: event.target.value as 'video' | 'post' })}><option value="video">Video or reel</option><option value="post">Post</option></select>
          </Setting>
          <FieldText label="Display title" value={item.title} onChange={(title) => updateFacebookItem(item.id, { title })} />
          <FieldFacebookUrl label="Facebook permalink" value={item.url} allowEmpty onChange={(url) => updateFacebookItem(item.id, { url })} />
          <div className="ed-dog-actions">
            <button type="button" className="ed-secondary-action" disabled={index === 0} onClick={() => moveFacebookItem(item.id, -1)}>Move up</button>
            <button type="button" className="ed-secondary-action" disabled={index === content.facebook.items.length - 1} onClick={() => moveFacebookItem(item.id, 1)}>Move down</button>
          </div>
          <RemoveButton label="Remove update" onClick={() => setContent((current) => ({ ...current, facebook: { ...current.facebook, items: current.facebook.items.filter((entry) => entry.id !== item.id) } }))} />
        </EditorBlock>
      ))}
      <div className="ed-field-grid">
        <AddBlockButton label="Add video" onClick={() => addFacebookItem('video')} />
        <AddBlockButton label="Add post" onClick={() => addFacebookItem('post')} />
      </div>
    </>
  );

  if (selected.id === 'process') return (
    <>
      <InspectorHeading title="How it works" description="Explain the path from application to gotcha day." />
      <FieldText label="Kicker" value={content.copy.processKicker} onChange={(value) => setCopy('processKicker', value)} />
      <FieldText label="Title" value={content.copy.processTitle} onChange={(value) => setCopy('processTitle', value)} />
      <FieldText label="Introduction" value={content.copy.processSubtext} onChange={(value) => setCopy('processSubtext', value)} area />
      <BlockDivider label="Steps" />
      {content.process.map((step, index) => (
        <EditorBlock key={`${step.step}-${index}`} title={`${step.step}. ${step.title}`} icon={<ListChecks size={16} />}>
          <FieldText label="Step" value={step.step} onChange={(value) => setContent((current) => ({ ...current, process: current.process.map((item, itemIndex) => itemIndex === index ? { ...item, step: value } : item) }))} />
          <FieldText label="Title" value={step.title} onChange={(value) => setContent((current) => ({ ...current, process: current.process.map((item, itemIndex) => itemIndex === index ? { ...item, title: value } : item) }))} />
          <FieldText label="Body" value={step.body} onChange={(value) => setContent((current) => ({ ...current, process: current.process.map((item, itemIndex) => itemIndex === index ? { ...item, body: value } : item) }))} area />
          <RemoveButton label="Remove step" onClick={() => setContent((current) => ({ ...current, process: current.process.filter((_, itemIndex) => itemIndex !== index) }))} />
        </EditorBlock>
      ))}
      <AddBlockButton label="Add step" onClick={() => setContent((current) => ({ ...current, process: [...current.process, { step: String(current.process.length + 1), title: 'New step', body: '' }] }))} />
    </>
  );

  if (selected.id === 'stories') return (
    <>
      <InspectorHeading title="Testimonials" description="Use verified family stories to build trust." />
      <FieldText label="Kicker" value={content.copy.testimonialsKicker} onChange={(value) => setCopy('testimonialsKicker', value)} />
      <FieldText label="Title" value={content.copy.testimonialsTitle} onChange={(value) => setCopy('testimonialsTitle', value)} />
      <BlockDivider label="Family stories" />
      {content.testimonials.map((testimonial, index) => (
        <EditorBlock key={`${testimonial.name}-${index}`} title={testimonial.name || `Family ${index + 1}`} subtitle={testimonial.location} icon={<Quote size={16} />}>
          <FieldText label="Quote" value={testimonial.quote} onChange={(value) => setContent((current) => ({ ...current, testimonials: current.testimonials.map((item, itemIndex) => itemIndex === index ? { ...item, quote: value } : item) }))} area />
          <FieldText label="Family name" value={testimonial.name} onChange={(value) => setContent((current) => ({ ...current, testimonials: current.testimonials.map((item, itemIndex) => itemIndex === index ? { ...item, name: value } : item) }))} />
          <FieldText label="Location" value={testimonial.location} onChange={(value) => setContent((current) => ({ ...current, testimonials: current.testimonials.map((item, itemIndex) => itemIndex === index ? { ...item, location: value } : item) }))} />
          <RemoveButton label="Remove testimonial" onClick={() => setContent((current) => ({ ...current, testimonials: current.testimonials.filter((_, itemIndex) => itemIndex !== index) }))} />
        </EditorBlock>
      ))}
      <AddBlockButton label="Add testimonial" onClick={() => setContent((current) => ({ ...current, testimonials: [...current.testimonials, { quote: '', name: 'New family', location: '' }] }))} />
    </>
  );

  if (selected.id === 'quiz') return (
    <>
      <InspectorHeading title="Readiness quiz" description="Set expectations before a family applies." />
      <FieldText label="Kicker" value={content.copy.quizKicker} onChange={(value) => setCopy('quizKicker', value)} />
      <FieldText label="Title" value={content.copy.quizTitle} onChange={(value) => setCopy('quizTitle', value)} />
      <FieldText label="Introduction" value={content.copy.quizSubtext} onChange={(value) => setCopy('quizSubtext', value)} area />
      <div className="ed-info-card"><ClipboardCheck size={18} /><span><b>Five-question readiness check</b><small>The scoring stays fixed so answers remain consistent across applicants.</small></span></div>
    </>
  );

  if (selected.id === 'litters') return (
    <>
      <InspectorHeading title="Upcoming litters" description="Manage public availability without leaving the theme editor." />
      <FieldText label="Kicker" value={content.copy.littersKicker} onChange={(value) => setCopy('littersKicker', value)} />
      <FieldText label="Title" value={content.copy.littersTitle} onChange={(value) => setCopy('littersTitle', value)} />
      <FieldText label="Introduction" value={content.copy.littersSubtext} onChange={(value) => setCopy('littersSubtext', value)} area />
      <BlockDivider label="Litters" />
      {litters.map((litter) => (
        <EditorBlock key={litter.id} title={litter.title || 'New pairing'} subtitle={`${litter.spotsLeft} ${litter.spotsLeft === 1 ? 'spot' : 'spots'} left`} icon={<Calendar size={16} />}>
          <FieldImage
            label="Cover image"
            src={litter.imageUrl ?? ''}
            alt=""
            crop={litter.imageCrop}
            onPick={() => openPicker((url) => {
              setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, imageUrl: url, imageCrop: null } : item));
              openCrop({
                src: url,
                label: `${litter.title || 'Litter'} cover`,
                aspectRatio: 16 / 9,
                value: null,
                defaultValue: LITTER_CROP_DEFAULT,
                apply: (crop) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, imageCrop: crop } : item)),
              });
            })}
            onCrop={() => openCrop({
              src: litter.imageUrl ?? '',
              label: `${litter.title || 'Litter'} cover`,
              aspectRatio: 16 / 9,
              value: litter.imageCrop,
              defaultValue: LITTER_CROP_DEFAULT,
              apply: (crop) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, imageCrop: crop } : item)),
            })}
            onRemove={() => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, imageUrl: null, imageCrop: null } : item))}
          />
          <FieldText label="Pairing / title" value={litter.title} onChange={(value) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, title: value } : item))} />
          <FieldText label="Description" value={litter.description} onChange={(value) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, description: value } : item))} area />
          <Setting label="Status"><select value={litter.status} onChange={(event) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, status: event.target.value as Litter['status'] } : item))}><option value="available">Accepting applications</option><option value="planned">Planned</option><option value="reserved">Fully reserved</option></select></Setting>
          <div className="ed-field-grid">
            <Setting label="Expected date"><input type="date" value={litter.expectedDate} onChange={(event) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, expectedDate: event.target.value } : item))} /></Setting>
            <Setting label="Spots left"><input type="number" min={0} value={litter.spotsLeft} onChange={(event) => setLitters((items) => items.map((item) => item.id === litter.id ? { ...item, spotsLeft: Number(event.target.value) } : item))} /></Setting>
          </div>
          <RemoveButton label="Remove litter" onClick={() => setLitters((items) => items.filter((item) => item.id !== litter.id))} />
        </EditorBlock>
      ))}
      <AddBlockButton label="Add litter" onClick={() => setLitters((items) => [...items, { id: crypto.randomUUID(), title: 'New pairing', description: '', expectedDate: '', spotsLeft: 0, status: 'planned', imageUrl: null, imageCrop: null }])} />
    </>
  );

  if (selected.id === 'faq') return (
    <>
      <InspectorHeading title="Frequently asked" description="Answer the questions families ask before applying." />
      <FieldText label="Kicker" field="faqKicker" value={content.copy.faqKicker} onChange={(value) => setCopy('faqKicker', value)} />
      <FieldText label="Title" field="faqTitle" value={content.copy.faqTitle} onChange={(value) => setCopy('faqTitle', value)} />
      <BlockDivider label="Questions" />
      {content.faq.map((item, index) => (
        <EditorBlock key={`${item.question}-${index}`} title={item.question || `Question ${index + 1}`} icon={<CircleHelp size={16} />}>
          <FieldText label="Question" value={item.question} onChange={(value) => setContent((current) => ({ ...current, faq: current.faq.map((faq, itemIndex) => itemIndex === index ? { ...faq, question: value } : faq) }))} />
          <FieldText label="Answer" value={item.answer} onChange={(value) => setContent((current) => ({ ...current, faq: current.faq.map((faq, itemIndex) => itemIndex === index ? { ...faq, answer: value } : faq) }))} area />
          <RemoveButton label="Remove question" onClick={() => setContent((current) => ({ ...current, faq: current.faq.filter((_, itemIndex) => itemIndex !== index) }))} />
        </EditorBlock>
      ))}
      <AddBlockButton label="Add question" onClick={() => setContent((current) => ({ ...current, faq: [...current.faq, { question: 'New question', answer: '' }] }))} />
    </>
  );

  if (selected.id === 'contact') return (
    <>
      <InspectorHeading title="Contact form" description="Give families a clear, lower-commitment way to get in touch." />
      <FieldText label="Kicker" value={content.copy.contactKicker} onChange={(value) => setCopy('contactKicker', value)} />
      <FieldText label="Title" value={content.copy.contactTitle} onChange={(value) => setCopy('contactTitle', value)} />
      <FieldText label="Introduction" value={content.copy.contactIntro} onChange={(value) => setCopy('contactIntro', value)} area />
      <FieldText label="Contact email" value={content.copy.contactEmail} onChange={(value) => setCopy('contactEmail', value)} />
      <FieldText label="Instagram" value={content.copy.contactInstagram} onChange={(value) => setCopy('contactInstagram', value)} />
    </>
  );

  return (
    <>
      <InspectorHeading title="Footer & newsletter" description="Close with location, contact, and litter updates." />
      <FieldText label="Breeder blurb" value={content.copy.footerBlurb} onChange={(value) => setCopy('footerBlurb', value)} area />
      <div className="ed-info-card"><Mail size={18} /><span><b>Litter announcement list</b><small>The signup can be toggled from App embeds.</small></span></div>
    </>
  );
}

function updateThemeLayout(setContent: SettingsProps['setContent'], patch: Partial<ThemeLayout>) {
  setContent((current) => ({ ...current, theme: { ...current.theme, layout: { ...current.theme.layout, ...patch } } }));
}

function updateThemeButtons(setContent: SettingsProps['setContent'], patch: Partial<ThemeButtons>) {
  setContent((current) => ({ ...current, theme: { ...current.theme, buttons: { ...current.theme.buttons, ...patch } } }));
}

function InspectorHeading({ title, description }: { title: string; description: string }) {
  return <div className="ed-inspector-heading"><h2>{title}</h2><p>{description}</p></div>;
}

function Setting({ label, hint, field, children }: { label: string; hint?: string; field?: string; children: ReactNode }) {
  return <label className="ed-field" data-field={field}><span className="ed-field-label">{label}{hint && <small>{hint}</small>}</span>{children}</label>;
}

function FieldText({ label, value, onChange, area, field }: { label: string; value: string; onChange: (value: string) => void; area?: boolean; field?: string }) {
  return <Setting label={label} field={field}>{area ? <textarea rows={3} value={value} onChange={(event) => onChange(event.target.value)} /> : <input value={value} onChange={(event) => onChange(event.target.value)} />}</Setting>;
}

function FieldFacebookUrl({ label, value, onChange, field, allowEmpty = false }: { label: string; value: string; onChange: (value: string) => void; field?: string; allowEmpty?: boolean }) {
  const errorId = useId();
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState('');
  useEffect(() => { setDraft(value); setError(''); }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed && allowEmpty) { onChange(''); setError(''); return; }
    const normalized = normalizeFacebookUrl(trimmed);
    if (!normalized) {
      setError('Paste a full, secure facebook.com video, reel, post, or page URL.');
      return;
    }
    setDraft(normalized);
    setError('');
    onChange(normalized);
  };

  const updateDraft = (next: string) => {
    setDraft(next);
    setError('');
    const trimmed = next.trim();
    if (!trimmed && allowEmpty) {
      onChange('');
      return;
    }
    const normalized = normalizeFacebookUrl(trimmed);
    if (normalized) onChange(trimmed);
  };

  return (
    <Setting label={label} hint="Only secure facebook.com links are accepted." field={field}>
      <input
        type="url"
        value={draft}
        aria-label={label}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => updateDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commit(); } }}
      />
      {error && <small id={errorId} className="ed-field-error" role="alert">{error}</small>}
    </Setting>
  );
}

function FieldList({ label, hint = 'One per line', value, onChange }: { label: string; hint?: string; value: string[]; onChange: (value: string[]) => void }) {
  return <Setting label={label} hint={hint}><textarea rows={4} value={value.join('\n')} onChange={(event) => onChange(event.target.value.split('\n'))} /></Setting>;
}

function FieldColor({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (value: string) => void }) {
  return (
    <Setting label={label} hint={hint}>
      <div className="ed-color-control">
        <label className="ed-swatch" style={{ background: value }}><input type="color" value={value} onChange={(event) => onChange(event.target.value)} aria-label={`${label} color`} /></label>
        <input className="ed-color-hex" value={value} onChange={(event) => onChange(event.target.value)} spellCheck={false} aria-label={`${label} hex value`} />
      </div>
    </Setting>
  );
}

function FieldImage({ label, src, alt, crop, onPick, onCrop, onRemove }: { label: string; src: string; alt: string; crop?: ImageCrop | null; onPick: () => void; onCrop: () => void; onRemove: () => void }) {
  return (
    <Setting label={label}>
      {src ? (
        <div className="ed-image-set">
          <button type="button" className="ed-image-button" onClick={onPick}><img className="ed-image-thumb" src={src} alt={alt} style={imageCropStyle(crop)} /><span>Change</span></button>
          <div className="ed-image-actions">
            <button type="button" className={`ed-crop-trigger ${crop ? 'is-custom' : ''}`} onClick={onCrop} aria-label={`Adjust crop for ${label.toLowerCase()}`}><Crop size={14} />Adjust crop{crop && <small>Custom</small>}</button>
            <button type="button" className="ed-text-action ed-danger" onClick={onRemove}>Remove image</button>
          </div>
        </div>
      ) : (
        <button type="button" className="ed-image-empty" onClick={onPick}><ImageIcon size={22} /><span>Select image</span></button>
      )}
    </Setting>
  );
}

function EditorBlock({ title, subtitle, icon, children }: { title: string; subtitle?: string; icon: ReactNode; children: ReactNode }) {
  return (
    <details className="ed-block">
      <summary><span className="ed-block-icon">{icon}</span><span><b>{title}</b>{subtitle && <small>{subtitle}</small>}</span><ChevronDown size={16} /></summary>
      <div className="ed-block-body">{children}</div>
    </details>
  );
}

function BlockDivider({ label }: { label: string }) {
  return <div className="ed-block-divider"><span>{label}</span></div>;
}

function AddBlockButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="ed-add-block" onClick={onClick}><Plus size={16} />{label}</button>;
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="ed-remove-block" onClick={onClick}><Trash2 size={15} />{label}</button>;
}

function ChoiceGroup({ value, options, onChange }: { value: string; options: Array<[string, string]>; onChange: (value: string) => void }) {
  return <div className="ed-choice-group">{options.map(([option, label]) => <button type="button" key={option} className={value === option ? 'on' : ''} aria-pressed={value === option} onClick={() => onChange(option)}>{label}</button>)}</div>;
}

function Switch({ checked, label, onChange }: { checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return <button type="button" className={`ed-switch ${checked ? 'on' : ''}`} role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}><span /></button>;
}
